import { useTranslation } from 'react-i18next';
import { useCheckIsUpdatePeriod } from '../../../../components/update-period-announcement-panel';
import type { ClassCardOptions } from '../../../components/class-card/class-card.options';
import {
  canDeleteClassroom,
  isClassroomCreatedManually,
  type Classroom,
} from '../../../modules/classroom';

type Props = {
  openEditModal: (classroom: Classroom) => void;
  handleDeleteClassWithConfirmation: (classroom: Classroom) => void;
};

type CreateOptions = {
  createClassroomOptions: (classroom: Classroom) => ClassCardOptions;
};

function useSchoolClassroomCreateOptions({
  openEditModal,
  handleDeleteClassWithConfirmation,
}: Props): CreateOptions {
  const { t } = useTranslation();
  const isUpdatePeriod = useCheckIsUpdatePeriod();

  const createClassroomOptions = (classroom: Classroom): ClassCardOptions => {
    const numberOfStudents = classroom.numberOfStudents || 0;
    const canDelete = canDeleteClassroom({
      classroom,
      isUpdatePeriod,
    });

    const hasStudents = numberOfStudents > 0;

    const isCreatedManually = isClassroomCreatedManually(classroom);

    const options: ClassCardOptions = {
      edit: {
        text: t('common.edit'),
        onClick: () => {
          openEditModal(classroom);
        },
        state: isUpdatePeriod ? 'disabled' : 'active',
      },
      delete: {
        text: t('classrooms.deleteClassroom'),
        subtitle: !isCreatedManually
          ? t('manager.delete-classroom-rostered.warning')
          : hasStudents
          ? t('my-classes.delete-classroom.warning')
          : undefined,
        onClick: () => handleDeleteClassWithConfirmation(classroom),
        state: canDelete ? 'active' : 'disabled',
      },
    };

    return options;
  };

  return { createClassroomOptions };
}

export { useSchoolClassroomCreateOptions };
